import { useMutation, useQuery, useQueryClient, type QueryFilters } from '@tanstack/react-query'
import { useMemo, useState, type PointerEvent as ReactPointerEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { Icon } from '../../components/Icon.js'
import { SortMenu } from '../../components/ListControls.js'
import { Popover, PopoverDivider, PopoverHeading, PopoverOption } from '../../components/Popover.js'
import {
  Avatar,
  Badge,
  ButtonGroup,
  EmptyState,
  ErrorState,
  FilterChip,
  SearchBox,
  Select,
  SkeletonRows,
  Toolbar,
} from '../../components/ui.js'
import { useBandDrag } from '../../lib/band-drag.js'
import { cn } from '../../lib/cn.js'
import { relativeTime } from '../../lib/format.js'
import { standingBadge } from '../../lib/licences.js'
import { useCurrentUser } from '../../lib/session.js'
import { errorMessage, useTRPC } from '../../lib/trpc.js'

/**
 * The people list, on the dispatch board's surface.
 *
 * Grouping is drawn client-side over the page that was loaded, and stays out of
 * the query key (decision 33) — forty staff arrive in one request, so a
 * regrouping is a re-render rather than a round trip. Search and sort go to the
 * server, because those change which rows exist.
 *
 * Dragging moves somebody between `ScheduleGroup`s, which are the same rows the
 * dispatch board draws its sections from: this screen and that one are editing
 * the same thing. It is enabled only while the grouping is Team, because a drag
 * onto a band that means "Technician" cannot be honoured. The gesture itself is
 * `useBandDrag`, and three parts of it are load-bearing: the whole row is the
 * grab rather than the grip, the row travels with the pointer, and the move is
 * written into the list's cache before the server answers so the person lands
 * in the band while the hand is still moving.
 */

export type PersonGroupBy = 'team' | 'role' | 'branch' | 'standing' | 'none'

const GROUPS: ReadonlyArray<{ key: PersonGroupBy; label: string }> = [
  { key: 'team', label: 'Team' },
  { key: 'role', label: 'Role' },
  { key: 'branch', label: 'Branch' },
  { key: 'standing', label: 'Ticket standing' },
  { key: 'none', label: 'None' },
]

type SortKey = 'name' | 'role' | 'team' | 'lastSeen' | 'expiry'

const SORTS: ReadonlyArray<{ key: SortKey; label: string }> = [
  { key: 'name', label: 'Name' },
  { key: 'role', label: 'Role' },
  { key: 'team', label: 'Team' },
  { key: 'lastSeen', label: 'Last seen' },
  { key: 'expiry', label: 'Soonest expiry' },
]

/** The band that collects people with no team, and the id its drop zone uses. */
const UNASSIGNED = '__unassigned__'

type Person = {
  id: string
  name: string
  email: string
  colour: string
  isSchedulable: boolean
  archivedAt: Date | null
  lastSeenAt: Date | null
  branchId: string | null
  scheduleGroupId: string | null
  sm8Uuid: string | null
  role: { id: string; name: string }
  _count: { credentials: number }
}

export function PeopleView({ onInvite }: { onInvite: () => void }) {
  const trpc = useTRPC()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const me = useCurrentUser()

  const [q, setQ] = useState('')
  const [groupBy, setGroupBy] = useState<PersonGroupBy>('team')
  const [sort, setSort] = useState<SortKey>('name')
  const [dir, setDir] = useState<'asc' | 'desc'>('asc')
  const [includeArchived, setIncludeArchived] = useState(false)
  const [onlyScheduled, setOnlyScheduled] = useState(false)
  const [onlyInvited, setOnlyInvited] = useState(false)
  const [onlyIssues, setOnlyIssues] = useState(false)
  const [onlyPlaceholders, setOnlyPlaceholders] = useState(false)
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set())

  const users = useQuery(
    trpc.users.list.queryOptions({ includeArchived, ...(q.trim() ? { q: q.trim() } : {}) }),
  )
  const teams = useQuery(trpc.users.scheduleGroups.queryOptions())
  const branches = useQuery(trpc.settings.branches.queryOptions())
  const standings = useQuery(trpc.licences.standingByUser.queryOptions())

  /**
   * Moving somebody, with the list corrected before the server answers.
   *
   * Without this the row springs back to the band it came from, sits there for
   * a round trip and then jumps — which reads as the drag having failed and
   * then being undone by somebody else. The snapshot goes back if the write
   * does fail, and either way the query is invalidated, because the server is
   * the one that knows whether the move was allowed.
   */
  const listFilter = trpc.users.list.queryFilter() as QueryFilters

  const setGroup = useMutation(
    trpc.users.setScheduleGroup.mutationOptions({
      onMutate: ({ userId, scheduleGroupId }) => {
        const snapshot = queryClient.getQueriesData<Person[]>(listFilter)

        for (const [key, data] of snapshot) {
          if (!data) continue
          queryClient.setQueryData(
            key,
            data.map((entry) => (entry.id === userId ? { ...entry, scheduleGroupId } : entry)),
          )
        }

        return snapshot
      },
      onError: (_error, _input, snapshot) => {
        for (const [key, data] of snapshot ?? []) queryClient.setQueryData(key, data)
      },
      onSettled: () => void queryClient.invalidateQueries(trpc.users.pathFilter()),
    }),
  )

  const teamName = useMemo(() => {
    const names = new Map<string, string>()
    for (const team of teams.data ?? []) names.set(team.id, team.name)
    return names
  }, [teams.data])

  const branchName = useMemo(() => {
    const names = new Map<string, string>()
    for (const branch of branches.data ?? []) names.set(branch.id, branch.name)
    return names
  }, [branches.data])

  const people = (users.data ?? []) as unknown as Person[]

  const filtered = useMemo(() => {
    return people.filter((person) => {
      if (onlyScheduled && !person.isSchedulable) return false
      if (onlyInvited && person._count.credentials > 0) return false
      // The fake ServiceM8 staff — parking rows the office was using as people,
      // all with an `@import.invalid` address. Findable so they can be retired,
      // rather than quietly fixed here.
      if (onlyPlaceholders && !person.email.endsWith('@import.invalid')) return false

      if (onlyIssues) {
        const worst = standings.data?.[person.id]?.worst
        if (worst !== 'expired' && worst !== 'missing' && worst !== 'undated') return false
      }

      return true
    })
  }, [people, onlyScheduled, onlyInvited, onlyIssues, onlyPlaceholders, standings.data])

  const sorted = useMemo(() => {
    const rows = [...filtered]
    const sign = dir === 'desc' ? -1 : 1

    rows.sort((a, b) => {
      switch (sort) {
        case 'role':
          return sign * (a.role.name.localeCompare(b.role.name) || a.name.localeCompare(b.name))
        case 'team': {
          const left = a.scheduleGroupId ? (teamName.get(a.scheduleGroupId) ?? '') : ''
          const right = b.scheduleGroupId ? (teamName.get(b.scheduleGroupId) ?? '') : ''
          return sign * (left.localeCompare(right) || a.name.localeCompare(b.name))
        }
        case 'lastSeen': {
          // Never signed in sorts last whichever way the arrow points: it is the
          // absence of a date, not the oldest one.
          const left = a.lastSeenAt ? new Date(a.lastSeenAt).getTime() : null
          const right = b.lastSeenAt ? new Date(b.lastSeenAt).getTime() : null
          if (left === null && right === null) return a.name.localeCompare(b.name)
          if (left === null) return 1
          if (right === null) return -1
          return sign * (right - left)
        }
        case 'expiry': {
          const left = standings.data?.[a.id]?.soonest ?? null
          const right = standings.data?.[b.id]?.soonest ?? null
          if (left === null && right === null) return a.name.localeCompare(b.name)
          if (left === null) return 1
          if (right === null) return -1
          return sign * (left - right)
        }
        default:
          return sign * a.name.localeCompare(b.name)
      }
    })

    return rows
  }, [filtered, sort, dir, teamName, standings.data])

  const bands = useMemo(() => {
    if (groupBy === 'none') return [{ id: 'all', label: '', people: sorted }]

    const buckets = new Map<string, { id: string; label: string; people: Person[] }>()

    // Every team gets a band even when it is empty, because an empty team is
    // exactly the one somebody needs to drag the first person into.
    if (groupBy === 'team') {
      for (const team of teams.data ?? []) {
        buckets.set(team.id, { id: team.id, label: team.name, people: [] })
      }
      buckets.set(UNASSIGNED, { id: UNASSIGNED, label: 'No team', people: [] })
    }

    for (const person of sorted) {
      const { id, label } = bandFor(person, groupBy, teamName, branchName, standings.data)
      const existing = buckets.get(id)
      if (existing) existing.people.push(person)
      else buckets.set(id, { id, label, people: [person] })
    }

    const list = [...buckets.values()]

    // Unassigned last: it is a holding pen, not a team.
    return groupBy === 'team'
      ? [...list.filter((band) => band.id !== UNASSIGNED), ...list.filter((band) => band.id === UNASSIGNED)]
      : list.sort((a, b) => a.label.localeCompare(b.label))
  }, [sorted, groupBy, teams.data, teamName, branchName, standings.data])

  const draggable = groupBy === 'team'

  const toggleBand = (id: string) =>
    setCollapsed((was) => {
      const next = new Set(was)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })

  const drag = useBandDrag({
    enabled: draggable,
    onDrop: (userId, band) => {
      const person = people.find((entry) => entry.id === userId)
      const scheduleGroupId = band === UNASSIGNED ? null : band
      if (!person || person.scheduleGroupId === scheduleGroupId) return

      // Dropping into a folded band would otherwise look like the person
      // vanishing, so the band it lands in opens.
      setCollapsed((was) => {
        if (!was.has(band)) return was
        const next = new Set(was)
        next.delete(band)
        return next
      })

      setGroup.mutate({ userId, scheduleGroupId })
    },
  })

  const carried = drag.carrying ? people.find((entry) => entry.id === drag.carrying?.id) : undefined

  const chips = (
    <>
      <FilterChip active={onlyScheduled} onClick={() => setOnlyScheduled((was) => !was)}>
        On schedule
      </FilterChip>
      <FilterChip active={onlyInvited} onClick={() => setOnlyInvited((was) => !was)}>
        Invited
      </FilterChip>
      <FilterChip active={onlyIssues} onClick={() => setOnlyIssues((was) => !was)}>
        Ticket issues
      </FilterChip>
      <FilterChip active={includeArchived} onClick={() => setIncludeArchived((was) => !was)}>
        Archived
      </FilterChip>
      <FilterChip active={onlyPlaceholders} onClick={() => setOnlyPlaceholders((was) => !was)}>
        Import placeholders
      </FilterChip>
    </>
  )

  return (
    <>
      <Toolbar>
        <div className="min-w-0 w-full sm:w-[240px] sm:flex-none">
          <SearchBox value={q} onChange={setQ} placeholder="Search people" className="w-full sm:w-full" />
        </div>

        <ButtonGroup label="How this list reads">
          <SortMenu fields={SORTS} sort={sort} dir={dir} onChange={(next) => {
            if (next.sort) setSort(next.sort)
            if (next.dir) setDir(next.dir)
          }} />
          <PersonGroupMenu value={groupBy} onChange={setGroupBy} />
        </ButtonGroup>

        <span className="mx-0.5 hidden h-6 w-px bg-ink-200 lg:block" />

        {chips}
      </Toolbar>

      {/* A move that the server refused. The row has already gone back to the
          band it came from, so without this the drag looks like it worked. */}
      {setGroup.error ? (
        <p role="alert" className="border-b border-red-200 bg-red-50 px-[var(--row-pad-x)] py-2 text-xs text-red-700">
          {errorMessage(setGroup.error)}
        </p>
      ) : null}

      {users.error ? (
        <ErrorState message={errorMessage(users.error)} />
      ) : users.isPending ? (
        <SkeletonRows />
      ) : sorted.length === 0 ? (
        <EmptyState
          title={q.trim() ? 'Nobody matches that' : 'No people'}
          description={
            q.trim()
              ? 'Try a different name or email address.'
              : 'Invite someone with the New person button.'
          }
          action={
            q.trim() ? undefined : (
              <button
                type="button"
                onClick={onInvite}
                className="text-xs font-medium text-brand-700 hover:underline"
              >
                Invite someone
              </button>
            )
          }
        />
      ) : (
        <div>
          <ColumnHeadings />
          {bands.map((band) => (
            <Band
              key={band.id}
              id={band.id}
              label={band.label}
              count={band.people.length}
              collapsed={collapsed.has(band.id)}
              onToggle={() => toggleBand(band.id)}
              droppable={draggable}
              carrying={drag.carrying !== null}
              over={drag.over === band.id && drag.carrying?.from !== band.id}
            >
              {band.people.map((person) => (
                <PersonRow
                  key={person.id}
                  person={person}
                  isMe={person.id === me.id}
                  teams={teams.data ?? []}
                  teamLabel={
                    person.scheduleGroupId
                      ? (teamName.get(person.scheduleGroupId) ?? '—')
                      : 'No team'
                  }
                  branchLabel={
                    person.branchId ? (branchName.get(person.branchId) ?? '—') : '—'
                  }
                  standing={standings.data?.[person.id] ?? null}
                  draggable={draggable}
                  lifted={drag.carrying?.id === person.id}
                  onGrab={(event) => drag.begin(event, person.id, band.id)}
                  onOpen={() => {
                    // The pointer-up that ended a drag still produces a click
                    // on the row it started in. That click is not a request to
                    // open anybody.
                    if (drag.dragged()) return
                    navigate(`/people/${person.id}`)
                  }}
                  onSetTeam={(scheduleGroupId) =>
                    setGroup.mutate({ userId: person.id, scheduleGroupId })
                  }
                />
              ))}
            </Band>
          ))}
        </div>
      )}

      {carried ? (
        <CarriedRow
          ref={drag.ghostRef}
          person={carried}
          // Over the band they are already in, there is nowhere to say.
          landing={drag.over && drag.over !== drag.carrying?.from ? drag.over : null}
          teamName={teamName}
        />
      ) : null}
    </>
  )
}

/* -------------------------------------------------------------------------- */

function bandFor(
  person: Person,
  groupBy: PersonGroupBy,
  teamName: Map<string, string>,
  branchName: Map<string, string>,
  standings: Record<string, { worst: string | null }> | undefined,
): { id: string; label: string } {
  switch (groupBy) {
    case 'team':
      return person.scheduleGroupId
        ? {
            id: person.scheduleGroupId,
            label: teamName.get(person.scheduleGroupId) ?? 'Unknown team',
          }
        : { id: UNASSIGNED, label: 'No team' }
    case 'role':
      return { id: person.role.id, label: person.role.name }
    case 'branch':
      return person.branchId
        ? { id: person.branchId, label: branchName.get(person.branchId) ?? 'Unknown branch' }
        : { id: '__nobranch__', label: 'No branch' }
    case 'standing': {
      const worst = standings?.[person.id]?.worst ?? null
      return worst === null
        ? { id: 'none', label: 'Nothing recorded' }
        : { id: worst, label: standingBadge(worst).label }
    }
    default:
      return { id: 'all', label: '' }
  }
}

/**
 * The column strip.
 *
 * Aligned columns rather than a paragraph per row: the question this screen
 * answers is "who is where", and a column of teams is readable down the page in
 * a way that the same word buried in a sentence is not.
 */
function ColumnHeadings() {
  return (
    <div className="hidden items-center gap-3 border-b border-ink-200 px-[var(--row-pad-x)] py-1.5 text-[10px] font-medium tracking-wide text-ink-400 uppercase lg:flex">
      <span className="w-4 shrink-0" aria-hidden />
      <span className="size-7 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1">Person</span>
      <span className="w-28 shrink-0">Role</span>
      <span className="w-28 shrink-0">Team</span>
      <span className="w-28 shrink-0">Branch</span>
      <span className="w-24 shrink-0">Tickets</span>
      <span className="w-20 shrink-0 text-center">Schedule</span>
      <span className="w-24 shrink-0">Last seen</span>
      <span className="w-8 shrink-0" aria-hidden />
    </div>
  )
}

/**
 * One team, and the drop target for it.
 *
 * The whole band is the target, heading and empty message included, because the
 * question a drag asks is "which team", not "which row of it". `data-band` is
 * all the gesture needs: it finds the band the pointer is inside by walking up
 * from whatever the pointer is actually over.
 */
function Band({
  id,
  label,
  count,
  collapsed,
  onToggle,
  droppable,
  carrying,
  over,
  children,
}: {
  id: string
  label: string
  count: number
  collapsed: boolean
  onToggle: () => void
  droppable: boolean
  /** Something is in flight, so an empty band shows where to put it. */
  carrying: boolean
  /** This band is the one a release would drop onto. */
  over: boolean
  children: React.ReactNode
}) {
  if (!label) return <ul className="divide-y divide-ink-100">{children}</ul>

  return (
    <section
      {...(droppable ? { 'data-band': id } : {})}
      className={cn('border-b border-ink-200 last:border-b-0', over && 'bg-brand-50')}
    >
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={!collapsed}
        className="flex w-full items-center gap-2 bg-ink-50 px-[var(--row-pad-x)] py-1.5 text-left transition-colors hover:bg-ink-100"
      >
        <Icon
          name="chevronDown"
          className={cn('size-3 text-ink-400 transition-transform', collapsed && '-rotate-90')}
          strokeWidth={2.25}
        />
        <span className="text-xs font-semibold text-ink-700">{label}</span>
        <span className="text-[11px] tabular-nums text-ink-400">{count}</span>
        {over ? (
          <span className="ml-auto text-[11px] font-medium text-brand-700">Move here</span>
        ) : null}
      </button>

      {collapsed ? null : count === 0 ? (
        <p
          className={cn(
            'px-[var(--row-pad-x)] py-3 text-xs text-ink-400',
            // While a row is in flight the empty band stops being a sentence
            // and becomes a place to aim at.
            carrying && droppable && 'm-2 rounded-lg border border-dashed border-ink-300 text-center',
            over && 'border-brand-500 text-brand-700',
          )}
        >
          {droppable ? (carrying ? 'Drop to move here' : 'Nobody here. Drag someone in.') : 'Nobody here.'}
        </p>
      ) : (
        <ul className="divide-y divide-ink-100">{children}</ul>
      )}
    </section>
  )
}

/**
 * The row under the pointer during a drag.
 *
 * Fixed to the viewport and positioned by the gesture rather than by React, so
 * a pointermove is a style write and not forty rows of reconciliation. Nothing
 * in it is hit-testable: it sits under the cursor, and a band has to be able to
 * see the pointer arrive.
 */
function CarriedRow({
  ref,
  person,
  landing,
  teamName,
}: {
  ref: React.Ref<HTMLDivElement>
  person: Person
  landing: string | null
  teamName: Map<string, string>
}) {
  const target = landing === UNASSIGNED ? 'No team' : landing ? teamName.get(landing) : null

  return (
    <div
      ref={ref}
      aria-hidden
      className="pointer-events-none fixed top-0 left-0 z-50 flex items-center gap-2 rounded-lg border border-ink-200 bg-white px-2.5 py-1.5 text-xs shadow-lg"
    >
      <Avatar name={person.name} colour={person.colour} />
      <span className="font-medium text-ink-900">{person.name}</span>
      {target ? <span className="text-ink-500">→ {target}</span> : null}
    </div>
  )
}

function PersonRow({
  person,
  isMe,
  teams,
  teamLabel,
  branchLabel,
  standing,
  draggable,
  lifted,
  onGrab,
  onOpen,
  onSetTeam,
}: {
  person: Person
  isMe: boolean
  teams: ReadonlyArray<{ id: string; name: string }>
  teamLabel: string
  branchLabel: string
  standing: { worst: string | null; held: number; soonest: number | null } | null
  draggable: boolean
  /** This row is the one in flight. */
  lifted: boolean
  onGrab: (event: ReactPointerEvent) => void
  onOpen: () => void
  onSetTeam: (scheduleGroupId: string | null) => void
}) {
  const invited = person._count.credentials === 0
  const badge = standing?.worst ? standingBadge(standing.worst) : null

  return (
    <li
      onPointerDown={draggable ? onGrab : undefined}
      className={cn(
        'flex items-center gap-3 px-[var(--row-pad-x)] py-[var(--row-pad-y)] transition-colors hover:bg-ink-50',
        draggable && 'cursor-grab',
        lifted && 'opacity-40',
      )}
    >
      {/* The grip says the row can be picked up. The row itself is the handle:
          asking somebody to hit a 16px column of dots is what made this look
          like it was not implemented. */}
      <span
        aria-label={draggable ? `Move ${person.name} to another team` : undefined}
        className={cn('w-4 shrink-0', draggable ? 'text-ink-300' : 'invisible')}
      >
        <Icon name="grip" className="size-4" />
      </span>

      <button type="button" onClick={onOpen} className="flex min-w-0 flex-1 items-center gap-3 text-left">
        <Avatar name={person.name} colour={person.colour} />
        <span className="min-w-0 flex-1">
          <span className="flex flex-wrap items-center gap-1.5">
            <span className="truncate text-sm font-medium text-ink-900">{person.name}</span>
            {isMe ? <Badge tone="blue">You</Badge> : null}
            {person.archivedAt ? <Badge>Archived</Badge> : null}
            {invited && !person.archivedAt ? <Badge tone="amber">Invited</Badge> : null}
            {person.sm8Uuid ? <Badge tone="purple">Imported</Badge> : null}
          </span>
          <span className="block truncate text-xs text-ink-500">{person.email}</span>
        </span>
      </button>

      <span className="hidden w-28 shrink-0 truncate text-xs text-ink-600 lg:block">
        {person.role.name}
      </span>

      {/* The keyboard path onto a team. Drag is a shortcut for people with a
          mouse; this is the one that always works. */}
      <label className="hidden w-28 shrink-0 lg:block">
        <span className="sr-only">{`${person.name}'s team`}</span>
        <Select
          value={person.scheduleGroupId ?? ''}
          onChange={(event) => onSetTeam(event.target.value || null)}
          className="min-h-[var(--control-sm)] border-0 bg-transparent px-0 text-xs text-ink-600 shadow-none ring-0 hover:text-ink-900 focus:ring-1"
        >
          <option value="">No team</option>
          {teams.map((team) => (
            <option key={team.id} value={team.id}>
              {team.name}
            </option>
          ))}
        </Select>
      </label>

      <span className="hidden w-28 shrink-0 truncate text-xs text-ink-600 lg:block">
        {branchLabel}
      </span>

      <span className="hidden w-24 shrink-0 lg:block">
        {badge ? (
          <Badge tone={badge.tone}>{badge.label}</Badge>
        ) : (
          <span className="text-xs text-ink-400">None</span>
        )}
      </span>

      <span className="hidden w-20 shrink-0 text-center text-xs text-ink-600 lg:block">
        {person.isSchedulable ? 'Yes' : 'No'}
      </span>

      <span className="hidden w-24 shrink-0 truncate text-xs text-ink-500 lg:block">
        {person.lastSeenAt ? relativeTime(person.lastSeenAt) : 'Never'}
      </span>

      <button
        type="button"
        onClick={onOpen}
        className="w-8 shrink-0 text-xs text-ink-400 hover:text-ink-700"
      >
        Open
      </button>

      {/* Everything the columns hide below `lg`, as one line under the name. */}
      <span className="text-xs text-ink-500 lg:hidden">{teamLabel}</span>
    </li>
  )
}

function PersonGroupMenu({
  value,
  onChange,
}: {
  value: PersonGroupBy
  onChange: (value: PersonGroupBy) => void
}) {
  const current = GROUPS.find((group) => group.key === value)

  return (
    <Popover
      grouped
      width={200}
      icon={<Icon name="group" className="size-3.5 text-ink-400" />}
      label={value === 'none' ? 'Group' : (current?.label ?? 'Group')}
      title={value === 'none' ? 'Group the list' : `Grouped by ${current?.label}`}
    >
      {(close) => (
        <div>
          <PopoverHeading>Group by</PopoverHeading>
          {GROUPS.map((group) => (
            <PopoverOption
              key={group.key}
              mark="disc"
              label={group.label}
              state={group.key === value ? 'all' : 'none'}
              onClick={() => {
                onChange(group.key)
                close()
              }}
            />
          ))}

          <PopoverDivider />
          <p className="px-2 pt-1 pb-1 text-[10px] leading-snug text-ink-500">
            {value === 'team'
              ? 'Drag a person onto another team to move them. It moves them on the dispatch board too.'
              : 'Group by Team to drag people between teams.'}
          </p>
        </div>
      )}
    </Popover>
  )
}
